import { useComposedRefs } from '@Page/Onboarding/hooks/useComposedRefs';
import { validate } from '@Page/Onboarding/validators/validation';
import React, { useEffect, useRef, useState } from 'react';
import { ValidatedProps } from '../ValidatedForm.types';

type ValidatedFormSelectProps = ValidatedProps &
    React.SelectHTMLAttributes<HTMLSelectElement> &
    React.RefAttributes<HTMLSelectElement>;

export function useValidatedFormSelect({ validator, ref, ...props }: ValidatedFormSelectProps) {
    const [errorMessage, setErrorMessage] = useState('');
    const selectRef = useRef<HTMLSelectElement>(null);
    const composedRef = useComposedRefs(selectRef, ref);
    const showError = !!errorMessage && !props.disabled;
    useEffect(() => {
        if (selectRef.current) {
            const error = validate(selectRef.current, validator);
            selectRef.current.setCustomValidity(error);
            // Doing it this way prevents the error showing up before the user has interacted with a dependent input field.
            if (error === '') {
                setErrorMessage(error);
            }
        }
        //TODO: This needs to run on every validation-related prop.
    }, [validator, props.required]);

    // event listener for form reset
    useEffect(() => {
        const form = selectRef.current?.form;
        if (form) {
            const resetListener = () => {
                setErrorMessage('');
            };
            form.addEventListener('reset', resetListener);
            return () => {
                form.removeEventListener('reset', resetListener);
            };
        }
    }, []);

    function onChange(e: React.ChangeEvent<HTMLSelectElement>) {
        const error = validate(selectRef.current, validator);
        setErrorMessage('');
        selectRef.current?.setCustomValidity(error);
        props.onChange?.(e);
        selectRef.current?.checkValidity();
    }

    //onInvalid handler because dropdown does not pass them down
    useEffect(() => {
        const element = selectRef.current;
        function onInvalid() {
            if (element) {
                setErrorMessage(element.validationMessage);
            }
        }

        if (element) {
            element.addEventListener('invalid', onInvalid);
            return () => {
                element.removeEventListener('invalid', onInvalid);
            };
        }
    }, []);

    return {
        errorMessage,
        showError,
        inputProps: {
            ...props,
            ref: composedRef,
            onChange,
        },
    };
}
